import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WindowSubscribeService } from '@efficiently/client-core-browser';

import { DeferredDispatchService } from './deferred-dispatch.service';

let isUnloadTriggered = false;

export function getIsUnloadTriggered(): boolean {
	return isUnloadTriggered;
}

/**
 * Coordinates work that must run when the tab/browser is closing or reloading:
 * - flips the unload flag so in-flight fetches can opt into `keepalive`,
 * - drains the deferred-dispatch queue so any queued NgXS actions still fire.
 *
 * In-flight GraphQL mutations are NOT re-sent here. Re-sending mutations on
 * `beforeunload` was racy: the original request often reaches the server even
 * when the browser cancels its socket, so the resend produced a duplicate
 * write for entities whose id is generated server-side (notably contacts).
 * Removing the resend is what eliminates the duplicate. The Apollo HTTP link
 * keeps the request alive across navigation by checking this unload flag at
 * fetch time; individual high-criticality short mutations can additionally
 * opt in to forced keepalive via `context.keepalive = true`.
 */
@Injectable({ providedIn: 'root' })
export class UnloadSyncService {
	private readonly deferredDispatchService = inject(DeferredDispatchService);
	private readonly windowSubscribeService = inject(WindowSubscribeService);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		this.windowSubscribeService.beforeUnload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			isUnloadTriggered = true;
			this.deferredDispatchService.dispatch();
		});
	}
}
